import { faEllipsisH, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import * as React from 'react';
import { Ref } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

const StyledButton = styled(Button)<{ $borderless?: boolean }>`
    display: inline-flex;
    align-items: center;
    transition: none;
    color: ${tokens.icon.actionActive};
    ${({ $borderless }) => ($borderless ? `border-width: 0px !important;` : '')}
    &:disabled {
        background-color: transparent !important;
        border-color: transparent !important;
    }
    &:hover {
        color: ${tokens.icon.actionHover};
        background-color: ${tokens.action.hover};
        border-color: ${tokens.divider.default};
    }
    &:active {
        // this probably won't display because of the event.preventDefault() below
        border-color: ${tokens.text.primary};
    }
`;

export type ActionMenuButtonProps = React.HTMLAttributes<HTMLButtonElement> &
    Readonly<{
        /**
         * The icon to use for the menu. Defaults to `faEllipsisH`.
         */
        icon?: IconDefinition;
        /**
         * CSS class name to apply to the button.
         */
        className?: string;
        borderless?: boolean;
    }>;

/**
 * The ActionMenuButton component
 */
export const ActionMenuButton = React.forwardRef(
    (
        {
            icon = faEllipsisH,
            className,
            onClick,
            'aria-label': ariaLabel = 'Open action menu',
            borderless = false,
            ...rest
        }: ActionMenuButtonProps,
        ref: Ref<HTMLButtonElement>,
    ) => (
        <StyledButton
            {...rest}
            ref={ref}
            className={className}
            aria-label={ariaLabel}
            $borderless={borderless}
            onClick={(event) => {
                // Action menu button often resides on top of clickable elements, so
                // it must prevent the click from propagating down to element below
                event.preventDefault();
                onClick?.(event);
            }}
        >
            <FontAwesomeIcon className="f-20" icon={icon} />
        </StyledButton>
    ),
);
